<template>
  <div class="client-page-container">
    <ClientModals v-if="client" ref="modals" :client-id="clientId" :client="client" :insights="insights" :groups="groups" :orderFields="orderFields" @refresh-rewards="loadRewardsReport" @refresh-forms="loadFormsReport" @refresh-tasks="loadTasksReport" @refresh-client="handleRefreshClient" :load-timeline="loadTimeline" />

    <div id="client-page">
      <KvScrollbar content-class="height-limit px-0">
        <ClientBasic :loading="!loaded" :canDelete="canDelete" :client="client" :timeline="timeline" @update-subscription="updateSubscription" @open-client-modal="openClient" @modal-open-order="openOrder" @modal-open-points="openPoints()" @modal-open-forms="openForms()" @modal-open-tasks="openTasks()" @loadData="(showLoader) => loadData(showLoader)" @delete-client="openDeleteClient" />
      </KvScrollbar>

      <div class="height-limit">
        <KvCard class="client-tabs" style="padding-top: 0;">
          <template #text>

            <KvTabs v-model="tab" grow>
              <template #tabs>
                <KvTab :value="1" style="padding-top: 0;">{{ t('clients-detail-insights') }}</KvTab>
                <KvTab :value="2" style="padding-top: 0;">{{ t('clients-detail-timeline') }}</KvTab>
                <KvTab :value="3" style="padding-top: 0;">{{ t('clients-detail-notes') }}</KvTab>
              </template>

              <template #panels>
                <KvTabPanel :value="1">
                  <ClientInsights :client="client" :sendAiMessage="sendAiMessage" :insights="insights" :loadRewardsReport="loadRewardsReport" :load-forms-report="loadFormsReport" :load-tasks-report="loadTasksReport" :load-coupons-report="loadCouponsReport" :load-notifications-report="loadNotificationsReport" :loadPredictionsReport="loadPredictionsReport" :load-purchases-report="loadPurchasesReport" @open-points-modal="openPoints" @open-forms-modal="openForms" @open-tasks-modal="openTasks" @open-order-modal="openOrder" :loading="insightsLoading || !loaded" />
                </KvTabPanel>

                <KvTabPanel :value="2">
                  <ClientTimeline :timeline="timeline" :load-timeline="loadTimeline" :client="client" :order-fields="orderFields" :loading="timelineLoading || !loaded" />
                </KvTabPanel>

                <KvTabPanel :value="3">
                  <ClientNotes :client="client" :notes="notes" :loading="notesLoading || !loaded" />
                </KvTabPanel>
              </template>
            </KvTabs>
          </template>
        </KvCard>
      </div>
    </div>
  </div>
</template>

<script>
import httpStatus from "@/helpers/httpStatus";
import keviApi from "@/keviApi";
import { loadPdvOrderFields, PDV_ORDER_FIELDS_URL } from "@/helpers/pdvOrderFields";

import ClientBasic from "./components/client/client-basic.vue";
import ClientInsights from "./components/client/client-insights.vue";
import ClientTimeline from "./components/client/client-timeline.vue";
import ClientNotes from "./components/client/client-notes.vue";
import ClientModals from "./components/client/client-modals.vue";

import KvTabs from "@/components/tabs/kv-tabs.vue";
import KvTab from "@/components/tabs/kv-tab.vue";
import KvTabPanel from "@/components/tabs/kv-tab-panel.vue";
import KvCard from "@/components/ui/kv-card.vue";
import { parseMarkdownToHtml } from "@/helpers/textFormatter";
import KvScrollbar from "@/components/ui/kv-scrollbar.vue";

export default {
  components: {
    ClientBasic,
    ClientInsights,
    ClientTimeline,
    ClientNotes,
    ClientModals,
    KvTabs,
    KvTab,
    KvTabPanel,
    KvCard,
    KvScrollbar
  },

  data() {
    return {
      loaded: false,
      client: null,
      timeline: null,
      insights: null,
      notes: null,
      tab: 1,
      groups: [],
      orderFields: [],
      fieldValues: {},
      tabMapping: {
        1: 'insights',
        2: 'timeline',
        3: 'notes'
      },
      tabMappingReverse: {
        'insights': 1,
        'timeline': 2,
        'notes': 3
      },
      insightsLoading: false,
      timelineLoading: false,
      notesLoading: false,
    };
  },
  created() {
    const tabName = this.$route.query.tab;

    if (tabName && this.tabMappingReverse[tabName]) {
      this.tab = this.tabMappingReverse[tabName];
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    normalizeCustomGroups() {
      if (!this.groups?.length || !this.client) return;

      const existingGroups = this.client.custom_fields || [];

      const normalizedGroups = this.groups.map(groupDef => {
        const clientGroup = existingGroups.find(
          g => g.group_id === groupDef.id
        );

        const clientFieldsList = clientGroup && clientGroup.fields
          ? Object.values(clientGroup.fields)
          : [];

        return {
          group_id: groupDef.id,
          group_name: groupDef.name,
          fields: groupDef.fields.map(fieldDef => {
            const foundField = clientFieldsList.find(
              cf => cf.clients_fields_id === fieldDef.id
            );

            return {
              ...fieldDef,
              value: foundField ? foundField.value : ""
            };
          })
        };
      });

      this.$set(this.client, "custom_fields", normalizedGroups);
    },
    handleRefreshClient(newClient) {
      this.client = newClient
    },
    // --- Métodos de Controle das Modais (via Ref) ---
    openPoints() {
      this.$refs.modals.openPoints();
    },
    openForms() {
      this.$refs.modals.openForms();
    },
    openTasks() {
      this.$refs.modals.openTasks();
    },
    openClient() {
      this.$refs.modals.openClient();
    },
    openOrder() {
      this.$refs.modals.openOrder();
    },
    openDeleteClient() {
      this.$refs.modals.openDeleteClient();
    },

    async loadGroups() {
      const options = keviApi.getOptions("grouped/client-fields", "GET");
      const response = await this.$http(options);
      this.groups = response.body;
    },
    async loadOrderFields() {
      try {
        this.orderFields = await loadPdvOrderFields(this.$store.state.place?.id, () =>
          this.$http(keviApi.getOptions(PDV_ORDER_FIELDS_URL, "GET")).then((response) => response.body)
        );
      } catch (e) {
        return;
      }
      this.orderFields.forEach(field => {
        if (field.type === 'checkbox') {
          this.$set(this.fieldValues, field.id, []);
        }
      });
    },
    // --- Carregamento de Dados ---
    async loadData(showLoader = true) {
      if (showLoader) {
        this.loaded = false;
      }

      // Order fields only label order rows on the timeline; load them in the
      // background instead of holding the page until they arrive.
      this.loadOrderFields();
      await Promise.all([this.loadDetail(), this.loadTimeline(), this.loadGroups()]);
      this.normalizeCustomGroups();

      this.handleTabChange(this.tab);
      this.loaded = true;
    },

    async loadDetail() {
      const response = await this.$http(
        keviApi.getOptions(`client/${this.clientId}/details`)
      );

      if (response.status === httpStatus.OK) {
        this.client = response.body.client;
      }
    },

    async loadTimeline({ page = this.$route.query.page || 1, limit = this.$route.query.limit || 50, search = this.$route.query.search || "", filters = this.$route.query.filter ? JSON.parse(this.$route.query.filter) : {}, keepData = false } = {}) {
      if (this.timeline && keepData) {
        return;
      }
      this.timelineLoading = true;
      try {
        const dateFilter = {};
        if (filters.period) {
          dateFilter.start = filters.period.start.split("T")[0];
          dateFilter.end = filters.period.end.split("T")[0];
        }

        const options = keviApi.getOptions(
          `place/client/${this.clientId}`,
          'GET',
          { page, limit, search, dateFilter }
        );

        const response = await this.$http(options);

        if (response.status === httpStatus.OK) {
          this.timeline = response.body;
        }
      } finally {
        this.timelineLoading = false;
      }
    },
    loadNotes(keepData = false) {
      if (this.notes && keepData) {
        return;
      }

      this.notesLoading = true;

      try {
        var options = keviApi.getOptions(
          `client/${this.clientId}/notes`,
          "GET",
        );
        this.$http(options).then(function (response) {
          this.notes = response.body.notes;
        });
      } finally {
        this.notesLoading = false;
      }
    },
    async sendAiMessage(question) {
      const id = `ai-${Date.now()}`;
      this.insights.messages.push({
        id: id,
        aiMessage: true,
        loading: true,
        content: "",
        date: null
      });

      try {
        const response = await this.$http(
          keviApi.getOptions(
            `clients/${this.clientId}/mcp-answer`,
            "GET",
            { question }
          )
        );

        if (response.status === httpStatus.OK) {
          const aiMessage = parseMarkdownToHtml(response.body.answer);
          const messageIndex = this.insights.messages.findIndex(m => m.id === id);

          if (messageIndex !== -1) {
            this.$set(this.insights.messages, messageIndex, {
              ...this.insights.messages[messageIndex],
              loading: false,
              content: aiMessage.content || aiMessage,
              date: new Date()
            });
            return id;
          }
        }
      } catch (err) {
        const messageIndex = this.insights.messages.findIndex(m => m.id === id);
        if (messageIndex !== -1) {
          this.$set(this.insights.messages, messageIndex, {
            ...this.insights.messages[messageIndex],
            loading: false,
            content: this.t('clients-detail-ai_error'),
            date: new Date()
          });
        }
      }
    },
    async loadInsights(keepData = false) {
      if (this.insights && keepData) {
        return;
      }

      this.insightsLoading = true;
      if (!this.insights) {
        this.insights = {
          messages: [],
        };
      }
      try {
        await Promise.all([
          this.loadPurchasesReport(),
          this.loadFormsReport(),
        ]);
      } finally {
        this.insightsLoading = false;
      }
      Promise.all([
        this.loadRewardsReport(),
        this.loadTasksReport(),
        this.loadCouponsReport(),
        this.loadNotificationsReport(),
        this.loadPredictionsReport(),
      ]).catch(() => {});
    },
    async loadPurchasesReport(period = "all_time") {
      const response = await this.$http(
        keviApi.getOptions(
          `clients/${this.clientId}/purchases-report`,
          "GET",
          { period: period }
        )
      );
      if (response.status === httpStatus.OK) {
        this.$set(this.insights, 'purchases_report', response.body);
      }
    },
    async loadNotificationsReport(period = "all_time") {
      const response = await this.$http(
        keviApi.getOptions(
          `clients/${this.clientId}/notifications-report`,
          "GET",
          { period: period }
        )
      );
      if (response.status === httpStatus.OK) {
        this.$set(this.insights, 'notifications_report', response.body);
      }
    },
    async loadPredictionsReport(limitType = "last") {
      const response = await this.$http(
        keviApi.getOptions(
          `clients/${this.clientId}/predictions-report`,
          "GET",
          { limitType }
        )
      );
      if (response.status === httpStatus.OK) {
        this.$set(this.insights, 'predictions_report', response.body);
      }
    },
    async loadRewardsReport(limitType = "last") {
      if (!this.$pointsProgram()) {
        return;
      }
      const response = await this.$http(
        keviApi.getOptions(
          `clients/${this.clientId}/rewards-report`,
          "GET",
          { period_all: "all_time", limitType }
        )
      );
      if (response.status === httpStatus.OK) {
        this.$set(this.insights, 'rewards_report', response.body);
      }
    },
    async loadFormsReport(limitType = "last") {
      const response = await this.$http(
        keviApi.getOptions(
          `clients/${this.clientId}/forms-report`,
          "GET",
          { period_all: "all_time", limitType }
        )
      );
      if (response.status === httpStatus.OK) {
        this.$set(this.insights, 'forms_report', response.body);
      }
    },
    async loadTasksReport(limitType = "last") {
      const response = await this.$http(
        keviApi.getOptions(
          `clients/${this.clientId}/tasks-report`,
          "GET",
          { period_all: "all_time", limitType }
        )
      );
      if (response.status === httpStatus.OK) {
        this.$set(this.insights, 'tasks_report', response.body);
      }
    },
    async loadCouponsReport(limitType = "last") {
      const response = await this.$http(
        keviApi.getOptions(
          `clients/${this.clientId}/coupons-report`,
          "GET",
          { period_all: "all_time", limitType }
        )
      );
      if (response.status === httpStatus.OK) {
        this.$set(this.insights, 'coupons_report', response.body);
      }
    },
    async updateSubscription(subscription) {
      this.client.subscribed_whatsapp = subscription.whatsapp;
      this.client.subscribed_sms = subscription.sms;
      this.client.subscribed_email = subscription.email;
      this.client.subscribed_rcs = subscription.rcs;
    },
    handleTabChange(tabValue) {
      if (tabValue === 1) {
        this.loadInsights(true);
      }
      // else if (tabValue === 2) {
      //   this.loadTimeline({ keepData: true });
      // }
      else if (tabValue === 3) {
        this.loadNotes(true);
      }
    }
  },

  computed: {
    clientId() {
      return this.$route.params.client_id;
    },
    canDelete() {
      const user = this.$store.state.user;
      return user && user.permissions && user.permissions.clients && parseInt(user.permissions.clients.options?.deletion) === 1;
    }
  },
  watch: {
    tab(newTab) {
      this.handleTabChange(newTab);

      const tabName = this.tabMapping[newTab];

      if (this.$route.query.tab !== tabName) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            tab: tabName
          }
        }).catch(() => { });
      }
    }
  }
};
</script>

<style lang="less">
.client-page-container {
  max-width: 1920px;
  margin: 0 auto;
}

#client-page {
  margin-left: 20px;
  margin-top: 0;
  margin-right: 0;

  display: grid;
  grid-template-columns: 40% 60%;
  gap: 8px;

  height: calc(100dvh - 80px);

  @media (max-width: 700px) {
    margin: 0 8px;

  }

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    height: 100%;
  }

  .height-limit {
    overflow-y: auto;
  }

  .client-tabs {
    &.kv-card {
      height: 100%;

      >.kv-card-content-wrapper {
        display: flex;
        flex-direction: column;
        min-height: 0;

        >.kv-expandable-content {
          display: flex;
          flex-direction: column;
          min-height: 0;

          >.kv-card-content-inner {
            display: flex;
            flex-direction: column;
            min-height: 0;
          }
        }
      }
    }


    .kv-tabs-container {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .kv-tab-panels-container {
      flex: 1;
      min-height: 0;
      display: flex;
    }

    .kv-card-content {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .kv-tab-panel {
      flex: 1;
      min-height: 0;
      width: 100%;
      overflow-y: auto;
    }
  }
}
</style>
